/**
 * Documents Tab Component - Multi-image upload support
 * Supports: Passport (2), Driver License (5), HMS Card (2), Certificates (5)
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  FileText,
  Shield,
  Upload,
  Camera,
  CheckCircle2,
  Clock,
  Trash2,
  IdCard,
  Car,
  GraduationCap,
  Plus,
  X,
  Eye,
  ImageIcon,
  Loader2,
  AlertCircle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Document type configurations
const DOCUMENT_CONFIG = {
  passport: {
    label: 'Identity Document',
    description: 'Passport or ID card (front & back)',
    maxImages: 2,
    slots: ['Front Side', 'Back Side'],
    icon: <IdCard className="w-5 h-5 text-blue-400" />,
    recommended: true
  },
  driver_license: {
    label: "Driver's License",
    description: 'All pages/sides of your license',
    maxImages: 5,
    slots: ['Front', 'Back', 'Additional 1', 'Additional 2', 'Additional 3'],
    icon: <Car className="w-5 h-5 text-green-400" />
  },
  hms_card: {
    label: 'HMS Card',
    description: 'Health, Safety & Environment card',
    maxImages: 2,
    slots: ['Front Side', 'Back Side'],
    icon: <Shield className="w-5 h-5 text-yellow-400" />,
    requiresConfirmation: true
  },
  certificate: {
    label: 'Certificates',
    description: 'Scaffolding courses and certifications',
    maxImages: 5,
    slots: ['Certificate 1', 'Certificate 2', 'Certificate 3', 'Certificate 4', 'Certificate 5'],
    icon: <GraduationCap className="w-5 h-5 text-purple-400" />
  }
};

export function DocumentsTab({ 
  profile, 
  uploadingDoc,
  handleUploadDocument,
  handleDeleteDocument,
  onRefreshProfile
}) {
  const [activeDocType, setActiveDocType] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploadingSlot, setUploadingSlot] = useState(null);

  // Get documents for a specific type (now supports multiple images per type)
  const getDocumentsForType = (docType) => {
    return profile.documents?.filter(d => d.type === docType) || [];
  };

  // Check if all slots are filled for a document type
  const getFilledSlotsCount = (docType) => {
    return getDocumentsForType(docType).length;
  };

  // Handle file selection for a specific slot
  const handleFileSelect = async (docType, slotIndex, file) => {
    if (!file) return;
    
    setUploadingSlot(`${docType}-${slotIndex}`);
    try {
      // Include slot info in the document name
      const config = DOCUMENT_CONFIG[docType];
      const slotName = config.slots[slotIndex];
      await handleUploadDocument(docType, file, slotName);
      
      // Refresh profile after upload
      if (onRefreshProfile) {
        await onRefreshProfile();
      }
    } finally {
      setUploadingSlot(null);
    }
  };

  // Render document type card
  const renderDocumentCard = (docType) => {
    const config = DOCUMENT_CONFIG[docType];
    const existingDocs = getDocumentsForType(docType);
    const filledCount = existingDocs.length;
    const isComplete = filledCount >= config.maxImages;
    
    // For HMS card, check confirmation status
    const isHmsConfirmed = docType === 'hms_card' && profile.hms_card_status === 'verified';
    
    return (
      <div key={docType} className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-white/20 transition-all">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {config.icon}
            <div>
              <p className="text-white font-medium flex items-center gap-2">
                {config.label}
                {config.recommended && (
                  <span className="text-yellow-400 text-xs px-2 py-0.5 bg-yellow-400/10 rounded-full">
                    Recommended
                  </span>
                )}
              </p>
              <p className="text-xs text-[#94A3B8]">{config.description}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Status Badge */}
            {filledCount > 0 ? (
              <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                isComplete 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-blue-500/20 text-blue-400'
              }`}>
                <ImageIcon className="w-3 h-3" />
                {filledCount}/{config.maxImages}
              </span>
            ) : (
              <span className="px-2 py-1 bg-white/10 text-[#94A3B8] rounded text-xs">
                Not uploaded
              </span>
            )}
            
            {/* HMS Confirmation Status */}
            {docType === 'hms_card' && filledCount > 0 && (
              <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                isHmsConfirmed 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {isHmsConfirmed ? (
                  <><CheckCircle2 className="w-3 h-3" /> Confirmed</>
                ) : (
                  <><Clock className="w-3 h-3" /> Awaiting</>
                )}
              </span>
            )}
            
            {/* Manage Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveDocType(docType)}
              className="border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/10"
              data-testid={`manage-${docType}-btn`}
            >
              {filledCount === 0 ? (
                <><Plus className="w-4 h-4 mr-1" /> Add</>
              ) : (
                <><Eye className="w-4 h-4 mr-1" /> Manage</>
              )}
            </Button>
          </div>
        </div>
        
        {/* Quick Preview of uploaded images */}
        {filledCount > 0 && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {existingDocs.slice(0, 4).map((doc, idx) => (
              <div 
                key={doc.document_id || idx}
                className="relative w-16 h-16 rounded-lg overflow-hidden bg-white/10 cursor-pointer hover:ring-2 ring-[#23F7DD]/50 transition-all"
                onClick={() => setPreviewImage(doc.url)}
              >
                {doc.url?.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                  <img 
                    src={doc.url.startsWith('http') ? doc.url : `${process.env.REACT_APP_BACKEND_URL}${doc.url}`}
                    alt={doc.name || 'Document'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-[#94A3B8]" />
                  </div>
                )}
                {doc.verified && (
                  <div className="absolute top-1 right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            ))}
            {filledCount > 4 && (
              <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center text-[#94A3B8] text-sm">
                +{filledCount - 4}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render document management modal
  const renderDocumentModal = () => {
    if (!activeDocType) return null;
    
    const config = DOCUMENT_CONFIG[activeDocType];
    const existingDocs = getDocumentsForType(activeDocType);
    
    return (
      <Dialog open={!!activeDocType} onOpenChange={() => setActiveDocType(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {config.icon}
              {config.label}
            </DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Upload up to {config.maxImages} images. {config.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Upload Slots */}
            {config.slots.map((slotName, slotIndex) => {
              const existingDoc = existingDocs[slotIndex];
              const isUploading = uploadingSlot === `${activeDocType}-${slotIndex}`;
              
              return (
                <div 
                  key={slotIndex}
                  className={`p-4 rounded-lg border transition-all ${
                    existingDoc 
                      ? 'bg-green-500/5 border-green-500/30' 
                      : 'bg-white/5 border-white/10 border-dashed'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        existingDoc ? 'bg-green-500/20' : 'bg-white/10'
                      }`}>
                        {existingDoc ? (
                          existingDoc.url?.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                            <img 
                              src={existingDoc.url.startsWith('http') ? existingDoc.url : `${process.env.REACT_APP_BACKEND_URL}${existingDoc.url}`}
                              alt={slotName}
                              className="w-full h-full object-cover rounded-lg cursor-pointer"
                              onClick={() => setPreviewImage(existingDoc.url)}
                            />
                          ) : (
                            <FileText className="w-5 h-5 text-green-400" />
                          )
                        ) : (
                          <span className="text-[#94A3B8] text-sm font-medium">{slotIndex + 1}</span>
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">{slotName}</p>
                        {existingDoc ? (
                          <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                            <span>{existingDoc.name || 'Uploaded'}</span>
                            {existingDoc.verified && (
                              <span className="text-green-400 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Verified
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-[#94A3B8]">No file uploaded</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {existingDoc ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreviewImage(existingDoc.url)}
                            className="text-[#23F7DD] hover:text-[#70FFEF]"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDocument(existingDoc.document_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* Upload Button */}
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*,.pdf,.heic,.heif"
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleFileSelect(activeDocType, slotIndex, e.target.files[0]);
                                }
                              }}
                              disabled={isUploading || uploadingDoc}
                            />
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/10 pointer-events-none"
                              disabled={isUploading || uploadingDoc}
                            >
                              {isUploading ? (
                                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading...</>
                              ) : (
                                <><Upload className="w-4 h-4 mr-1" /> Upload</>
                              )}
                            </Button>
                          </div>
                          
                          {/* Camera Button (Mobile) */}
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleFileSelect(activeDocType, slotIndex, e.target.files[0]);
                                }
                              }}
                              disabled={isUploading || uploadingDoc}
                            />
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 pointer-events-none"
                              disabled={isUploading || uploadingDoc}
                            >
                              <Camera className="w-4 h-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* HMS Card Special Note */}
            {activeDocType === 'hms_card' && (
              <div className={`p-4 rounded-lg border ${
                profile.hms_card_status === 'verified'
                  ? 'bg-green-500/10 border-green-500/30'
                  : 'bg-yellow-500/10 border-yellow-500/30'
              }`}>
                <div className="flex items-start gap-3">
                  {profile.hms_card_status === 'verified' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-medium ${
                      profile.hms_card_status === 'verified' ? 'text-green-400' : 'text-yellow-400'
                    }`}>
                      {profile.hms_card_status === 'verified' 
                        ? 'HMS Card Confirmed' 
                        : 'HMS Card Awaiting Confirmation'}
                    </p>
                    <p className="text-sm text-[#94A3B8] mt-1">
                      {profile.hms_card_status === 'verified'
                        ? 'Your HMS card has been verified by the Holding Admin.'
                        : 'Once you are approved as a worker, your HMS status will be automatically confirmed.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end mt-6">
            <Button
              onClick={() => setActiveDocType(null)}
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Image preview modal
  const renderPreviewModal = () => {
    if (!previewImage) return null;
    
    const imageUrl = previewImage.startsWith('http') 
      ? previewImage 
      : `${process.env.REACT_APP_BACKEND_URL}${previewImage}`;
    
    return (
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-4xl p-2">
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <img 
            src={imageUrl}
            alt="Document Preview"
            className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
          />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="space-y-6">
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#23F7DD]" />
            Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-[#94A3B8]">
            Upload your identity documents for verification. You can upload multiple images for each document type.
            Admin will review and approve your account.
          </p>

          {/* Document Type Cards */}
          <div className="space-y-4">
            {Object.keys(DOCUMENT_CONFIG).map(docType => renderDocumentCard(docType))}
          </div>

          {/* HMS Card Overall Status */}
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-[#23F7DD]" />
                <div>
                  <p className="text-white font-medium">HMS Card Status</p>
                  <p className="text-sm text-[#94A3B8]">Health, Safety & Environment certification</p>
                </div>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium border ${
                  profile.hms_card_status === "verified"
                    ? "bg-green-500/20 text-green-400 border-green-500/30"
                    : profile.hms_card_status === "expired"
                    ? "bg-red-500/20 text-red-400 border-red-500/30"
                    : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                }`}
              >
                {profile.hms_card_status === "verified" ? "Verified" : 
                 profile.hms_card_status === "expired" ? "Expired" : "Pending"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Modals */}
      {renderDocumentModal()}
      {renderPreviewModal()}
    </div>
  );
}
